import {Button, Input, Modal, notification, Table, Tag, Tooltip} from "antd";
import {useEffect, useRef, useState} from "react";
import {ReloadOutlined, SearchOutlined} from "@ant-design/icons";
import {showConfirm} from "../../../utils/popup.utils";
import {useAuth} from "oidc-react";
import {findKVPValue, splitValue} from "../../../utils/kvp.utils";
import SharedService from "../../../services/shared/service";

const Label = ({children}) => (
    <div className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide mb-1.5">
        {children}
    </div>
);

const PurchasePopup = ({visible, invoiceDetailData, onClose, onAddClick}) => {
    const {userData} = useAuth();
    const token = userData?.access_token;

    const [purchaseOrder, setPurchaseOrder] = useState([]);
    const [purchaseNumberIdValue, setPurchaseNumberIdValue] = useState("");
    const [coyIdValue, setCoyIdValue] = useState("");
    const [customerName, setCustomerName] = useState("");
    const [purchaseData, setPurchaseData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [listPagination, setListPagination] = useState({
        totalItemCount: 0,
        pageSize: 10,
        pageNumber: 1,
    });
    const [sortedInfo, setSortedInfo] = useState({});
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [refreshButtonDisabled, setRefreshButtonDisabled] = useState(true);
    const [searchFormValues, setSearchFormValues] = useState({
        purchaseOrderNumber: "",
        accountingCoyId: "",
        entityType: "",
    });

    const searchParams = useRef({});
    let splittedPurchaseNumber = "";

    const handleSearchFormChange = (e) => {
        const {name, value} = e.target;
        setSearchFormValues((prev) => ({...prev, [name]: value}));
    };

    const handleSearchFormRefresh = () => {
        initialisePurchaseOrderData();
        setRefreshButtonDisabled(true);
        setListPagination((prev) => ({...prev, pageNumber: 1}));
        fetchPurchaseOrder();
    };

    const handleSearchFormSubmit = (e) => {
        e.preventDefault();
        setRefreshButtonDisabled(false);

        const data = {};
        if (searchFormValues.purchaseOrderNumber) data.purchaseOrderNumber = searchFormValues.purchaseOrderNumber;
        if (searchFormValues.accountingCoyId) {
            data.accountingCoyId = searchFormValues.accountingCoyId;
            if (searchFormValues.entityType) {
                data.entityType = searchFormValues.entityType;
            }
        }

        searchParams.current = data;
        setListPagination((prev) => ({...prev, pageNumber: 1}));
        fetchPurchaseOrder(false);
    };

    const initialisePurchaseOrderData = async () => {
        const initCustomerName = findKVPValue("CustomerName", invoiceDetailData);
        const customerType = findKVPValue("CustomerType", invoiceDetailData);
        const vesselId = findKVPValue("VesselId", invoiceDetailData);
        const supplierId = findKVPValue("SupplierId", invoiceDetailData);
        const supplierName = findKVPValue("SupplierName", invoiceDetailData);
        const coyId = findKVPValue("CoyId", invoiceDetailData);
        const purchaseOrderNo = splitValue(findKVPValue("PurchaseOrder", invoiceDetailData));

        const data = {};
        if (purchaseOrderNo) data.purchaseOrderNumber = purchaseOrderNo;
        if (vesselId) data.vesselId = vesselId;
        if (supplierId) data.supplierId = supplierId;
        if (supplierName) data.supplierName = supplierName;
        if (coyId) data.accountingCoyId = coyId;
        if (customerType) data.entityType = customerType;

        setCustomerName(initCustomerName);
        setPurchaseNumberIdValue(purchaseOrderNo);
        setCoyIdValue(coyId);
        setSearchFormValues((prev) => ({
            ...prev,
            accountingCoyId: coyId ?? "",
            purchaseOrderNumber: purchaseOrderNo ?? "",
            entityType: customerType ?? "",
        }));
        searchParams.current = data;
    };

    useEffect(() => {
        initialisePurchaseOrderData();
    }, [invoiceDetailData]);

    useEffect(() => {
        fetchPurchaseOrder();
    }, [invoiceDetailData]);

    useEffect(() => {
        if (listPagination.totalItemCount !== 0) fetchPurchaseOrder();
    }, [listPagination.pageNumber, sortedInfo]);

    const fetchPurchaseOrder = async (retry = true) => {
        setLoading(true);
        try {
            const params = {
                pageNumber: listPagination.pageNumber,
                pageSize: listPagination.pageSize,
                select: "id,number,accountingCoyId,accountingCoyName,name,totalCost,supplier,status,stage,vessel,notes",
            };

            let response = await SharedService.post({
                token,
                url: "purchaseorder/search",
                data: searchParams.current,
                params,
            });

            let PurchaseOrderData = response?.items ?? [];
            let totalCount = response?.totalItemCount ?? 0;

            if (PurchaseOrderData.length === 0 && retry) {
                const data = {};
                if (purchaseNumberIdValue) data.purchaseOrderNumber = purchaseNumberIdValue;
                searchParams.current = data;

                response = await SharedService.post({
                    token,
                    url: "purchaseorder/search",
                    data: searchParams.current,
                    params,
                });
                PurchaseOrderData = response?.items ?? [];
                totalCount = response?.totalItemCount ?? 0;
            }

            const nextExpanded = [];
            PurchaseOrderData.forEach((item) => {
                if (item?.notes) nextExpanded.push(item.id);
            });
            setExpandedRowKeys(nextExpanded);

            setPurchaseOrder(PurchaseOrderData);
            setListPagination((prev) => ({...prev, totalItemCount: totalCount}));
        } catch (error) {
            console.error(error);
            if (error?.response?.status === 500) {
                notification.error({message: "Error", description: error.response.statusText});
            }
        } finally {
            setLoading(false);
        }
    };

    const onConfirm = () => {
        if (purchaseData.length > 0) onAddClick(purchaseData[0]);
    };

    const handleTableChange = (pagination, _filters, sorter) => {
        setSortedInfo(sorter);
        setListPagination((prev) => ({...prev, pageNumber: pagination.current}));
    };

    const handleConfirm = () => {
        const purchaseOrderKvp = invoiceDetailData.find((item) => item.key === "PurchaseOrder");
        const purchaseDataValue = purchaseOrderKvp?.correctedValue ?? purchaseOrderKvp?.value;

        if (purchaseDataValue) {
            const purchaseSplit = purchaseDataValue.split("-");
            splittedPurchaseNumber = purchaseSplit.length > 1 ? purchaseSplit[1] : purchaseDataValue;
        }

        const invoiceData = invoiceDetailData.find((item) => item.key === "SupplierName");
        const invoiceDataValue = invoiceData?.correctedValue ?? invoiceData?.value;
        const vesselData = invoiceDetailData.find((item) => item.key === "VesselName");
        const vesselNameValue = vesselData?.correctedValue ?? vesselData?.value;

        const supplierMismatch = invoiceDataValue && invoiceDataValue !== purchaseData[0]?.supplier?.name && invoiceDataValue !== "";
        const vesselMismatch = vesselNameValue && vesselNameValue !== purchaseData[0]?.vessel?.name;
        const poMismatch = splittedPurchaseNumber && splittedPurchaseNumber !== purchaseData[0]?.number;

        if (supplierMismatch || poMismatch || vesselMismatch) {
            showConfirm({
                className: "small-popup",
                closable: true,
                okText: "Yes",
                cancelText: "No",
                onOk: onConfirm,
                title: "Confirm purchase order selection",
                content: (
                    <div className="flex flex-col gap-3 text-[14px] text-[#374152]">
                        {supplierMismatch && (
                            <div className="rounded-md p-3" style={{background: "#FFF4E0"}}>
                                <div>
                                    You have selected a purchase order from supplier{" "}
                                    <span className="font-semibold text-[#0B1220]">{invoiceDataValue}</span>.
                                </div>
                                <div className="mt-1">
                                    Now selected supplier is{" "}
                                    <span className="font-semibold text-[#0B1220]">{purchaseData[0]?.supplier?.name}</span>.
                                </div>
                            </div>
                        )}
                        {vesselMismatch && (
                            <div className="rounded-md p-3" style={{background: "#FFF4E0"}}>
                                <div>
                                    New vessel name:{" "}
                                    <span className="font-semibold text-[#0B1220]">{purchaseData[0]?.vessel?.name}</span>
                                </div>
                                <div className="mt-1">
                                    Invoice OCR vessel name:{" "}
                                    <span className="font-semibold text-[#0B1220]">{vesselNameValue}</span>
                                </div>
                            </div>
                        )}
                        {poMismatch && (
                            <div className="rounded-md p-3" style={{background: "#FFF4E0"}}>
                                You have selected PO{" "}
                                <span className="font-semibold text-[#0B1220]">{purchaseData[0]?.number}</span>{" "}
                                for customer {customerName}, which differs from PO{" "}
                                <span className="font-semibold text-[#0B1220]">{splittedPurchaseNumber}</span>{" "}
                                referenced by the supplier.
                            </div>
                        )}
                        <div className="font-semibold text-[#0B1220] text-[15px] mt-1">
                            Do you want to change the
                            {supplierMismatch && <span> Supplier Id</span>}
                            {vesselMismatch && (
                                <span>{supplierMismatch && ","} OCR Vessel name</span>
                            )}
                            {poMismatch && <span> and PO number</span>}?
                        </div>
                    </div>
                ),
            });
        } else {
            onConfirm();
        }
    };

    const columns = [
        {
            title: "PO Number",
            dataIndex: "number",
            key: "number",
            width: 140,
            sorter: true,
            sortOrder: sortedInfo.columnKey === "number" && sortedInfo.order,
            render: (text) => <span className="cell-mono">{text}</span>,
        },
        {
            title: "Accounting Co.",
            dataIndex: "accountingCoyId",
            key: "accountingCoyId",
            width: 140,
            sorter: true,
            sortOrder: sortedInfo.columnKey === "accountingCoyId" && sortedInfo.order,
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            width: 280,
            sorter: true,
            sortOrder: sortedInfo.columnKey === "name" && sortedInfo.order,
        },
        {
            title: "Value",
            dataIndex: "totalCost",
            key: "totalCost",
            width: 130,
            align: "right",
            render: (text) => <span className="cell-amt">{text?.toLocaleString() ?? "—"}</span>,
        },
        {
            title: "Entity",
            dataIndex: "vessel",
            key: "vessel",
            width: 140,
            sorter: true,
            sortOrder: sortedInfo.columnKey === "vessel" && sortedInfo.order,
            render: (vessel) => vessel?.name,
        },
        {
            title: "Supplier",
            dataIndex: "supplier",
            key: "supplier",
            width: 260,
            sorter: true,
            sortOrder: sortedInfo.columnKey === "supplier" && sortedInfo.order,
            render: (supplier) => supplier?.name,
        },
        {
            title: "Stage",
            dataIndex: "stage",
            key: "stage",
            width: 160,
            render: (stage) => stage?.name,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) =>
                status ? (
                    <Tooltip title={status?.name}>
                        <Tag style={{background: "#F1F3F7", color: "#374152", border: "none", borderRadius: 6}}>
                            {status.id}
                        </Tag>
                    </Tooltip>
                ) : null,
        },
    ];

    const rowSelection = {
        type: "radio",
        selectedRowKeys,
        onChange: (selectedKeys, selectedRows) => {
            setPurchaseData(selectedRows);
            setSelectedRowKeys(selectedKeys);
        },
    };

    return (
        <Modal
            title="Purchase Orders"
            open={visible}
            onCancel={onClose}
            width="min(1600px, 95vw)"
            closable={true}
            footer={[
                <Button key="cancel" onClick={onClose}>Cancel</Button>,
                <Button
                    key="add"
                    type="primary"
                    onClick={handleConfirm}
                    disabled={purchaseData.length === 0}
                >
                    Use Selected PO
                </Button>,
            ]}
        >
            {(customerName || purchaseNumberIdValue) && (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 pb-3 border-b border-[#F1F3F7]">
                    {customerName && (
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="text-[11px] font-semibold text-[#7A849A] uppercase tracking-wide">
                                Customer
                            </span>
                            <Tooltip title={customerName}>
                                <span className="text-[13px] font-medium text-[#0B1220] truncate max-w-[420px]">
                                    {coyIdValue ? `${coyIdValue} · ` : ""}{customerName}
                                </span>
                            </Tooltip>
                        </div>
                    )}
                    {purchaseNumberIdValue && (
                        <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold text-[#7A849A] uppercase tracking-wide">
                                PO
                            </span>
                            <span className="cell-mono text-[13px] font-semibold text-[#0B1220]">
                                {purchaseNumberIdValue}
                            </span>
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] gap-3 gap-y-4 mb-4">
                <div>
                    <Label>Accounting Company ID</Label>
                    <Input
                        name="accountingCoyId"
                        placeholder="e.g. COY123"
                        value={searchFormValues.accountingCoyId}
                        onChange={handleSearchFormChange}
                        onPressEnter={handleSearchFormSubmit}
                    />
                </div>
                <div>
                    <Label>PO Number</Label>
                    <Input
                        name="purchaseOrderNumber"
                        placeholder="e.g. 4488210"
                        value={searchFormValues.purchaseOrderNumber}
                        onChange={handleSearchFormChange}
                        onPressEnter={handleSearchFormSubmit}
                    />
                </div>
                <div className="flex items-end gap-2">
                    <Button
                        type="primary"
                        icon={<SearchOutlined/>}
                        onClick={handleSearchFormSubmit}
                        loading={loading}
                    >
                        Search
                    </Button>
                    {!refreshButtonDisabled && (
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={handleSearchFormRefresh}
                            disabled={loading}
                        >
                            Reset
                        </Button>
                    )}
                </div>
            </div>

            <Table
                dataSource={purchaseOrder}
                columns={columns}
                loading={loading}
                pagination={{
                    showSizeChanger: false,
                    total: listPagination.totalItemCount,
                    pageSize: listPagination.pageSize,
                    current: listPagination.pageNumber,
                }}
                rowKey={(record) => record?.id}
                rowSelection={rowSelection}
                onChange={handleTableChange}
                size="middle"
                expandable={{
                    defaultExpandedRowKeys: [...expandedRowKeys],
                    expandedRowClassName: (record) =>
                        selectedRowKeys.includes(record.id) ? "selected-expanded-row" : "",
                    expandedRowRender: (record) => (
                        <Tooltip title={record?.notes}>
                            <p style={{margin: 0, fontSize: 12.5, color: "#374152"}}>
                                <strong className="text-[#0B1220]">Notes: </strong>
                                {record?.notes && record.notes.length > 500
                                    ? `${record.notes.slice(0, 500)}...`
                                    : record?.notes}
                            </p>
                        </Tooltip>
                    ),
                    rowExpandable: (record) => Boolean(record?.notes),
                }}
                scroll={{x: 1400, y: 500}}
                locale={{emptyText: "No purchase orders match these criteria."}}
            />
        </Modal>
    );
};

export default PurchasePopup;
